import React, { useState, useRef } from 'react';
import CalendarBookingModal from './TextBookingModal';
import BookingRequestModal from './BookingRequestModal';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { getSelectedAgentId, getSelectedAgent } from '@/store/account/selector';
import { openModal, closeModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { BookingOptionsModal } from './components/BookingOptionsModal';
import { WorkflowHeader } from './components/WorkflowHeader';
import { createWorkflowHandlers } from './handlers/WorkflowHandlers';
import { ExistingBookingWorkflows } from './components/ExistingBookingCard';
import { CalendarOptionsSelector } from './components/CalendarOptionSelect';
import { CustomModal } from '@/components/CustomModal';
import { CalendarSetupForm } from '@/components/CalendarSetup/CalendarSetupForm';
import { useQuery } from '@tanstack/react-query';
import { getCalendars } from '@/api/calendar';
import { handleCalendarSetup } from './handlers/CalendarHandlers';
import { getBookingRequestWorkflows, getTextBookingWorkflows } from '@/utils/workflowUtils';
import {
	ICalendarSetupValues,
	ICalendarWorkflowContainerProps,
	IWorkflow,
} from '@/interfaces/ICalendarWorkflows';
import { TModalRef } from '@/types/TCalendarWorkflows';
import { EWorkflowViewFilter } from 'src/enums/ECalendarWorkflows';

export const CalendarWorkflowContainer: React.FC<ICalendarWorkflowContainerProps> = ({
	clientObject,
	isNoviUser,
	debrand,
	mutation,
	refetch,
}) => {
	const [editingWorkflow, setEditingWorkflow] = useState<IWorkflow | null>(null);
	const [showOptionsModal, setShowOptionsModal] = useState(false);
	const calendarModalRef = useRef<TModalRef>(null);
	const bookingRequestModalRef = useRef<TModalRef>(null);

	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const dispatch = useAppDispatch();

	const { data: calendarData, refetch: refetchCalendars } = useQuery({
		queryKey: ['get-calendar', selectedAgentId],
		queryFn: () => (selectedAgentId ? getCalendars(selectedAgentId) : Promise.resolve(null)),
		enabled: !!selectedAgentId,
	});

	const existingCalendars = calendarData?.data?.calendars || [];
	const textBookingWorkflows = getTextBookingWorkflows(clientObject);
	const bookingRequestWorkflows = getBookingRequestWorkflows(clientObject);
	const hasAnyBookingWorkflows =
		textBookingWorkflows.length > 0 ||
		bookingRequestWorkflows.length > 0 ||
		existingCalendars.length > 0;

	const { handleDeleteTextBooking, handleDeleteBookingRequest } = createWorkflowHandlers(
		clientObject,
		mutation,
		refetch,
		selectedAgentId,
	);

	const handleSendBookingLink = () => {
		setShowOptionsModal(false);
		setEditingWorkflow(null);
		setTimeout(() => {
			calendarModalRef.current?.open();
		}, 100);
	};

	const handleCollectBookingInfo = () => {
		setShowOptionsModal(false);
		setEditingWorkflow(null);
		setTimeout(() => {
			bookingRequestModalRef.current?.open();
		}, 100);
	};

	const handleOverPhoneScheduling = () => {
		setShowOptionsModal(false);
		dispatch(openModal({ modalId: MODAL_IDS.CALENDAR_FORM_MODAL }));
	};

	const handleEditTextBooking = (workflow: IWorkflow) => {
		setEditingWorkflow(workflow);
		setTimeout(() => {
			calendarModalRef.current?.open(workflow);
		}, 100);
	};

	const handleEditBookingRequest = (workflow: IWorkflow) => {
		setEditingWorkflow(workflow);
		setTimeout(() => {
			bookingRequestModalRef.current?.open(workflow);
		}, 100);
	};

	const handleAddNewWorkflow = () => {
		setShowOptionsModal(true);
	};

	const handleCalendarSetupWrapper = async (values: ICalendarSetupValues) => {
		if (!selectedAgentId) {
			return;
		}

		await handleCalendarSetup({
			values,
			existingCalendars,
			selectedAgent,
			selectedAgentId,
			dispatch,
			onRefetch: refetch,
			onRefetchCalendars: refetchCalendars,
			closeModal: () => dispatch(closeModal()),
		});
	};

	return (
		<div className="h-full p-4">
			<WorkflowHeader
				hasAnyBookingWorkflows={hasAnyBookingWorkflows}
				onAddNewWorkflow={handleAddNewWorkflow}
			/>

			{hasAnyBookingWorkflows ? (
				<div className="h-full overflow-hidden">
					<div className="h-full overflow-y-auto pr-2">
						<ExistingBookingWorkflows
							clientObject={clientObject}
							onEditTextBooking={handleEditTextBooking}
							onEditBookingRequest={handleEditBookingRequest}
							onDeleteTextBooking={handleDeleteTextBooking}
							onDeleteBookingRequest={handleDeleteBookingRequest}
							viewFilter={EWorkflowViewFilter.ALL}
							onRefetch={refetch}
						/>
					</div>
				</div>
			) : (
				<CalendarOptionsSelector
					onOverPhoneScheduling={handleOverPhoneScheduling}
					onSendBookingLink={handleSendBookingLink}
					onCollectBookingInfo={handleCollectBookingInfo}
					existingCalendars={calendarData?.data?.calendars || []}
				/>
			)}

			<BookingOptionsModal
				open={showOptionsModal}
				onCancel={() => setShowOptionsModal(false)}
				onSendBookingLink={handleSendBookingLink}
				onCollectBookingInfo={handleCollectBookingInfo}
				onOverPhoneScheduling={handleOverPhoneScheduling}
				existingCalendars={calendarData?.data?.calendars || []}
			/>

			<CalendarBookingModal
				ref={calendarModalRef}
				clientObject={clientObject}
				mutation={mutation}
				refetch={refetch}
				debrand={debrand}
				isNoviUser={isNoviUser}
				editingWorkflow={editingWorkflow}
			/>

			<BookingRequestModal
				ref={bookingRequestModalRef}
				clientObject={clientObject}
				mutation={mutation}
				refetch={refetch}
				editingWorkflow={editingWorkflow}
			/>

			<CustomModal
				title="Calendar Setup"
				width="50%"
				children={
					<CalendarSetupForm
						onFinish={handleCalendarSetupWrapper}
						onCancel={() => dispatch(closeModal())}
						existingCalendars={existingCalendars}
					/>
				}
				footer={null}
				modalId={MODAL_IDS.CALENDAR_FORM_MODAL}
			/>
		</div>
	);
};
